import { useMemo } from "react";
import { useCombinedStore } from "./useCombinedStore";
import { useBusinesses } from "./useBusinesses";
import { SearchEntity } from "../../components/Search/globalSearch.entinty";

type FilterType = SearchEntity | "all";

export interface GlobalSearchItem {
  id: string;
  type: SearchEntity;
  title: string;
  description?: string;
  keywords?: string[];
  route: string;
  image?: string;
  state?: any;
}

const normalize = (value: any) =>
  String(value || "")
    .toLowerCase()
    .trim();

const includesQuery = (query: string, ...values: any[]) => {
  const q = normalize(query);
  return values.some((value) => normalize(value).includes(q));
};

const resolveId = (item: any) => item?.id || item?._id;

export const useGlobalSearch = (query: string, filter: FilterType = "all") => {
  const combined = useCombinedStore() as any;
  const { businesses } = useBusinesses();

  const products = combined?.products || [];
  const hikes = combined?.hikes || [];
  const accommodations = combined?.accommodations || [];
  const carHire = combined?.carHire || combined?.cars || [];
  const orders = combined?.orders || [];

  return useMemo(() => {
    const q = normalize(query);
    if (!q) return [];

    const items: GlobalSearchItem[] = [
      ...products.map((item: any) => ({
        id: resolveId(item),
        type: "products" as const,
        title: item?.title || item?.name || item?.productName || "Product",
        description: item?.description || item?.category || "",
        keywords: [item?.category, item?.location, item?.businessName].filter(
          Boolean,
        ),
        route: `/product/${resolveId(item)}`,
        image: item?.image || item?.images?.[0],
        state: { product: item },
      })),
      ...hikes.map((item: any) => ({
        id: resolveId(item),
        type: "hikes" as const,
        title: item?.title || item?.tripName || item?.name || "Trip",
        description:
          item?.description || `${item?.from || ""} ${item?.to || ""}`.trim(),
        keywords: [item?.from, item?.to, item?.location].filter(Boolean),
        route: `/search-details/hikes/${resolveId(item)}`,
        image: item?.image || item?.images?.[0],
        state: { hike: item },
      })),
      ...accommodations.map((item: any) => ({
        id: resolveId(item),
        type: "accommodations" as const,
        title:
          item?.title ||
          item?.name ||
          item?.accommodationName ||
          "Accommodation",
        description: item?.description || item?.location || item?.address || "",
        keywords: [item?.location, item?.address, item?.city].filter(Boolean),
        route: `/search-details/accommodations/${resolveId(item)}`,
        image: item?.image || item?.images?.[0],
        state: { accommodation: item },
      })),
      ...carHire.map((item: any) => ({
        id: resolveId(item),
        type: "carHire" as const,
        title: item?.title || item?.name || item?.carName || "Car Hire",
        description: item?.description || item?.location || item?.carType || "",
        keywords: [item?.location, item?.carType, item?.brand].filter(Boolean),
        route: `/search-details/cars/${resolveId(item)}`,
        image: item?.image || item?.images?.[0],
        state: { car: item },
      })),
      ...orders.map((item: any) => ({
        id: resolveId(item),
        type: "orders" as const,
        title: item?.title || item?.orderId || item?.id || "Order",
        description: item?.category || item?.status || "",
        keywords: [item?.status, item?.category].filter(Boolean),
        route: `/search-details/orders/${resolveId(item)}`,
        state: { order: item },
      })),
      ...businesses.map((item) => ({
        id: item.id,
        type: "businesses" as const,
        title: item.businessName || item.name || "Business",
        description: item.businessAddress || item.role || "",
        keywords: [
          item.name,
          item.businessName,
          item.businessAddress,
          item.phoneNumber,
          item.role,
        ].filter(Boolean),
        route: `/search-details/businesses/${item.id}`,
        image: item.logoURL,
        state: { business: item },
      })),
    ];

    return items.filter((item) => {
      const matchesFilter = filter === "all" || item.type === filter;
      if (!matchesFilter) return false;

      return includesQuery(
        q,
        item.title,
        item.description,
        ...(item.keywords || []),
      );
    });
  }, [
    query,
    filter,
    products,
    hikes,
    accommodations,
    carHire,
    orders,
    businesses,
  ]);
};
